import React, { useState, useEffect, CSSProperties } from "react";

// You might need to install lucide-react or use an SVG directly
// For this example, I'll use a direct SVG for the icon as shown in the image.

interface FileUploadProps {
  onChange: (file: File | null) => void;
  label?: string; // This will be "Upload a file here"
  accept?: string; // e.g., ".pdf,.docx"
  rules?: {
    required?: boolean | string;
    validate?: (file: File | null) => boolean | string;
  };
  className?: string;
  style?: CSSProperties;
  topLabel?: string; // For "Request Letter"
  fileTypesText?: string; // For "File types"
}

const FileUpload: React.FC<FileUploadProps> = ({
  onChange,
  label = "Upload a file here", // Default to the image's text
  accept,
  rules = {},
  className = "",
  style = {},
  fileTypesText = "File types", // Default for the file types text
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validate = (file: File | null): string | null => {
    if (rules.required && !file) {
      return typeof rules.required === "string"
        ? rules.required
        : "File is required";
    }
    if (rules.validate) {
      const result = rules.validate(file);
      if (result !== true) {
        return typeof result === "string" ? result : "Invalid file";
      }
    }
    return null;
  };

  useEffect(() => {
    setError(validate(file));
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    onChange(selected);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const selected = e.dataTransfer.files?.[0] || null;
    setFile(selected);
    onChange(selected);
  };

  return (
    <div className={`w-full ${className}`} style={style}>
      <div className="flex items-center mb-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-5 h-5 text-blue-500 mr-2" // Adjust color as needed
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.852l.708-2.836a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.852l.708-2.836a.75.75 0 011.063.852l-.708 2.836c.045.273.185.52.395.707l1.01.999a.75.75 0 01-.04.996l-3.25 1.75a.75.75 0 01-.98-.02l-1.002-1.002A.75.75 0 009.75 18a.75.75 0 01-.75-.75v-4.5a.75.75 0 011.5 0v4.25l.988-1.04a.75.75 0 011.03-.02l1.25 1.25a.75.75 0 01-.02 1.03L9.75 21a.75.75 0 01-1.03-.02L7.75 19.97c-.046-.046-.067-.11-.05-.175l.135-.54a.75.75 0 01.75-.75h1.25a.75.75 0 01.75.75z"
          />
        </svg>
        <span className="block  text-sm font-medium text-[#747171]">
          {label}
        </span>
        {rules.required && <span className="text-red-500 ml-1">*</span>}
      </div>
      <p className="text-sm text-gray-500 mb-2">{fileTypesText}</p>

      <label
        id={`dropzone-file ${label}`}
        className={`flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 d hover:bg-gray-10`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          {/* File icon with plus as seen in the image */}
          <svg
            className="w-12 h-12 mb-3 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2ZM13 10V14H15V10H13ZM11 10V14H9V10H11Z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15 13H17V15H15V17H13V15H11V13H13V11H15V13Z"
              fill="#fff"
            />
          </svg>
          <p className="mb-2 text-md text-gray-600 dark:text-gray-400">
            {file ? (
              <span className="font-semibold">{file.name}</span>
            ) : (
              <span className="font-medium">Upload a file here</span>
            )}
          </p>
          {file && (
            <button
              onClick={() => {
                setFile(null);
                onChange(null);
              }}
              className="text-sm text-red-500 hover:underline mt-1"
            >
              Remove file
            </button>
          )}
        </div>
        <input
          id={`dropzone-file ${label}`}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileChange}
        />
      </label>
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-500">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;

